import { gql, useMutation } from '@apollo/client';
import IconArrowRight from '@apollo/icons/default/IconArrowRight.svg';
import IconClose from '@apollo/icons/default/IconClose.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconSearch from '@apollo/icons/default/IconSearch.svg';
import IconStarSolid from '@apollo/icons/default/IconStarSolid.svg';
import {
  Accordion,
  AccordionDescription,
  AccordionHeader,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AccordionTitle,
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Code,
  FormControl,
  Group,
  IconButton,
  Input,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
// TODO: Update to use Orbit's hook instead
// eslint-disable-next-line no-restricted-imports
import { useToast } from '@chakra-ui/react';
import { useFormik } from 'formik';
import { LocationDescriptor } from 'history';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import {
  graphSettingsRouteConfig,
  variantSettingsRouteConfig,
} from 'src/app/graph/routes';
import IconEmptyStateMoon from 'src/assets/emptystate_moon.svg';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { CustomToast } from 'src/components/customToast/CustomToast';
import { useDebouncedEffect } from 'src/hooks/useDebouncedEffect';
import { useShowCompositionRules } from 'src/lib/featureFlags';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  BuildPipelineTrack,
  LintDiagnosticLevel,
  LintRule,
  LinterRuleCategory,
  LinterRuleLevelConfiguration,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import { AUTOSAVE_TIMEOUT_MS } from '../../checksConfigPage/constants';
import { UseGraphSettingsButton } from '../../variantChecksConfigPage/common/UseGraphSettingsButton';
import { LinterRuleDescriptionMarkdownRenderer } from '../linterRuleDescriptionMarkdownRenderer/LinterRuleDescriptionMarkdownRenderer';
import { SharedLinterCheckConfigProps } from '../shared';

import { ExampleCode } from './exampleCode/ExampleCode';
import { genericSearchRegexFactory } from './genericSearchRegexFactory/genericSearchRegexFactor';
import {
  BulkSeveritySelector,
  SeveritySelector,
} from './severitySelector/SeveritySelector';

// Graphs on federation version 2.3 or older do not have support
// for composition rules.
const compositionRulesUnsupportedBPTs = [
  BuildPipelineTrack.FED_2_3,
  BuildPipelineTrack.FED_2_1,
  BuildPipelineTrack.FED_2_0,
  BuildPipelineTrack.FED_1_1,
  BuildPipelineTrack.FED_1_0,
];

export const LinterRulesCardFragment = gql`
  fragment LinterRulesCardFragment on GraphLinterConfiguration {
    rules {
      rule
      level
      description
      goodExampleCode
      badExampleCode
      category
    }
  }
`;

const validationSchema = Yup.object().shape({
  rules: Yup.array(
    Yup.object({
      rule: Yup.mixed<GraphQLTypes.LintRule>().oneOf(Object.values(LintRule)),
      description: Yup.string(),
      level: Yup.mixed<GraphQLTypes.LintDiagnosticLevel>().oneOf(
        Object.values(LintDiagnosticLevel),
      ),
      goodExampleCode: Yup.string().nullable(true),
      badExampleCode: Yup.string().nullable(true),
    }),
  ).required(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const LinterRulesCard = ({
  isVariantConfig,
  isFederated,
  allVariants,
  linterConfig,
  graphRef,
}: SharedLinterCheckConfigProps & {
  isFederated: boolean;
  allVariants: GraphQLTypes.LinterConfigPageQuery_service_variants[];
}) => {
  const [search, setSearch] = useState('');

  const toast = useToast();
  const [updateLinterConfiguration] = useMutation<
    GraphQLTypes.LinterConfigRuleSeverityMutation,
    GraphQLTypes.LinterConfigRuleSeverityMutationVariables
  >(gql`
    mutation LinterConfigRuleSeverityMutation(
      $graphId: ID!
      $changes: GraphLinterConfigurationChangesInput!
    ) {
      service(id: $graphId) {
        id
        updateLinterConfiguration(changes: $changes) {
          rules {
            rule
            level
          }
        }
      }
    }
  `);
  const { values, errors, dirty, setFieldValue, submitForm } =
    useFormik<FormValues>({
      enableReinitialize: true,
      initialValues: {
        rules: linterConfig.rules,
      },
      onSubmit: async (formValues) => {
        const updatedRules = formValues.rules
          .filter((formValueRule) => {
            const matchingLinterConfigValueRule = linterConfig.rules.find(
              (linterConfigRule) =>
                formValueRule.rule === linterConfigRule.rule,
            );
            // compare current BE level with the newly changed level
            return (
              formValueRule.level !== matchingLinterConfigValueRule?.level ||
              false
            );
          })
          .map((rule) => ({ rule: rule.rule, level: rule.level }));

        await updateLinterConfiguration({
          variables: {
            graphId: graphRef.graphId,
            changes: {
              rules: updatedRules,
            },
          },
          update: (cache) => {
            cache.evict({
              id: cache.identify({
                __typename: 'Service',
                id: graphRef.graphId,
              }),
              fieldName: 'linterConfiguration',
            });
          },
        });

        toast({
          duration: 3000,
          position: 'bottom-right',
          isClosable: true,
          render: ({ onClose }) => (
            <CustomToast
              onClose={onClose}
              heading="Linter rules changed"
              description={
                <p>
                  You've successfully updated the severity of the rules for your
                  graph.
                </p>
              }
            />
          ),
        });
      },
      validationSchema,
    });

  const submitIfDirty = useCallback(() => {
    if (dirty && _.isEmpty(errors)) {
      submitForm();
    }
  }, [dirty, errors, submitForm]);

  useDebouncedEffect(submitIfDirty, AUTOSAVE_TIMEOUT_MS, [values]);

  // Rules grouped by category.
  const rulesGrouped = useMemo(
    () => _.groupBy(_.sortBy(linterConfig.rules, 'rule'), 'category'),
    [linterConfig.rules],
  );

  const currentLevelForRuleMap: Record<LintRule, LintDiagnosticLevel> = useMemo(
    () =>
      values.rules.reduce(
        (acc, rule) => ({ ...acc, [rule.rule]: rule.level }),
        {} as Record<LintRule, LintDiagnosticLevel>,
      ),
    [values.rules],
  );

  // Returns the rules in a category that match the search query.
  const filteredRulesByCategory = useCallback(
    (category: LinterRuleCategory) => {
      const searchFilterRegex = genericSearchRegexFactory(search);
      return (rulesGrouped[category] ?? [])
        .filter(
          (rule) =>
            !!searchFilterRegex.exec(rule.rule.toLowerCase()) ||
            !!searchFilterRegex.exec(rule.description.toLowerCase()),
        )
        .map((rule) => ({
          ...rule,
          level: currentLevelForRuleMap[rule.rule] || rule.level,
        }));
    },
    [search, rulesGrouped, currentLevelForRuleMap],
  );

  // To update a single rule.
  const onSelectNewSeverity = useCallback(
    (ruleName: string) => (newSeverity: GraphQLTypes.LintDiagnosticLevel) => {
      setFieldValue(
        'rules',
        values.rules.map((rule) =>
          rule.rule === ruleName ? { ...rule, level: newSeverity } : rule,
        ),
      );
    },
    [setFieldValue, values.rules],
  );

  // To Update the severity for the whole category.
  const [categorySeverity, setCategorySeverity] = useState<
    Record<LinterRuleCategory, LintDiagnosticLevel | undefined>
  >(
    // By default every category has no severity. When the severity is set
    // for the category, the "Apply" button is enabled, otherwise if
    // severity is `undefined` by this category then nothing to apply.
    Object.values(LinterRuleCategory).reduce(
      (acc, rule) => ({ ...acc, [rule]: undefined }),
      {} as Record<
        GraphQLTypes.LinterRuleCategory,
        LintDiagnosticLevel | undefined
      >,
    ),
  );

  const onSetCategorySeverity = useCallback(
    (category: LinterRuleCategory, severity: LintDiagnosticLevel | undefined) =>
      setCategorySeverity((prevState) => ({
        ...prevState,
        [category]: severity,
      })),
    [setCategorySeverity],
  );

  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);

  // To update the severity for an entire category.
  const onBulkUpdateSeverity = useCallback(
    (category: LinterRuleCategory, rules: LinterRuleLevelConfiguration[]) => {
      if (categorySeverity[category] === undefined) {
        // Severity for this category not set, nothing to do.
        return;
      }
      setFieldValue(
        'rules',
        rules
          .filter((rule) => rule.category === category)
          .map((rule) => ({
            ...rule,
            level: categorySeverity[rule.category],
          })),
      );
      // Reset it after updating.
      onSetCategorySeverity(category, undefined);
    },
    [categorySeverity, onSetCategorySeverity, setFieldValue],
  );

  // Functionality to expand/collapse the categories.
  const collapseCategories = useCallback((shouldCollapse: boolean) => {
    if (shouldCollapse) {
      setExpandedCategories([]);
    } else {
      setExpandedCategories(Object.keys(LinterRuleCategory).map((__, i) => i));
    }
  }, []);

  // Perform after a search query.
  useDebouncedEffect(() => collapseCategories(search.length === 0), 200, [
    search,
  ]);

  // This feature is still in active development so anything
  // related to composition rules should be hidden from users.
  const shouldShowCompositionRules = useShowCompositionRules();

  // Some graphs may not have a build (ie. monographs)
  const showCompositionRules =
    shouldShowCompositionRules &&
    isFederated &&
    linterConfig.rules.filter(
      (rule) => rule.category === LinterRuleCategory.COMPOSITION,
    ).length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rule severity configuration</CardTitle>
        <CardDescription>
          Adjust your linter rules' severity levels to Error, Warn, or Off.
          Filter the rules in each category by searching for rule names or
          descriptions.{' '}
          <Link isExternal to={DocsRouter.path('LinterConfiguration')}>
            Learn more
          </Link>
        </CardDescription>
        {isVariantConfig && (
          <FormControl>
            <UseGraphSettingsButton
              label="Use Graph Settings"
              tooltipContent="At this time, variant level overrides for the linter configuration are not available. Please make changes at the graph level."
              isSelected={true}
              // NOTE: We are only including graph level configuration at this
              // time so we should keep this disabled and leave the variant version
              // of this component in a read only state
              disabled={true}
              onChange={_.noop}
              trackingEventCategory="Lint Checks"
            />
          </FormControl>
        )}

        <Input
          className="mt-2 w-1/2"
          size="md"
          placeholder="Search by rule name and description..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          leftElement={<IconSearch className="pointer-events-none" />}
          rightElement={
            search.length > 0 && (
              <IconButton
                aria-label="Close"
                size="sm"
                variant="hidden"
                onClick={() => {
                  setSearch('');
                  collapseCategories(true);
                }}
              >
                <IconClose />
              </IconButton>
            )
          }
        />
      </CardHeader>
      <CardBody>
        <Accordion
          allowMultiple
          index={expandedCategories}
          onChange={(index: number[]) => setExpandedCategories(index)}
        >
          {/* Naming rules */}
          <RuleGroup
            graphRef={graphRef}
            title="Naming rules"
            description={
              <>
                These rules enforce naming conventions. Adjust their severity to
                reflect your organization's schema conventions.{' '}
                <Link isExternal to={DocsRouter.path('LinterRules')}>
                  Learn more
                </Link>
              </>
            }
            isVariantConfig={isVariantConfig}
            rules={filteredRulesByCategory(LinterRuleCategory.NAMING)}
            category={LinterRuleCategory.NAMING}
            bulkSeverity={categorySeverity[LinterRuleCategory.NAMING]}
            allVariants={allVariants}
            onSelectSeverity={onSelectNewSeverity}
            onSelectBulkSeverity={(severity) =>
              onSetCategorySeverity(LinterRuleCategory.NAMING, severity)
            }
            onApplyBulkSeverity={() =>
              onBulkUpdateSeverity(
                LinterRuleCategory.NAMING,
                filteredRulesByCategory(LinterRuleCategory.NAMING),
              )
            }
          />

          {/* Composition rules */}
          {showCompositionRules && (
            <RuleGroup
              graphRef={graphRef}
              title="Composition rules"
              description={
                <>
                  These rules are generated during{' '}
                  <Link
                    isExternal
                    to={DocsRouter.path('FederationComposition')}
                  >
                    composition
                  </Link>
                  . Adjust their severity to flag potential improvements to your
                  supergraph schema.{' '}
                  <Link
                    isExternal
                    to={DocsRouter.path('LinterCompositionRules')}
                  >
                    Learn more
                  </Link>
                </>
              }
              isVariantConfig={isVariantConfig}
              rules={filteredRulesByCategory(LinterRuleCategory.COMPOSITION)}
              category={LinterRuleCategory.COMPOSITION}
              bulkSeverity={categorySeverity[LinterRuleCategory.COMPOSITION]}
              allVariants={allVariants}
              onSelectSeverity={onSelectNewSeverity}
              onSelectBulkSeverity={(severity) =>
                onSetCategorySeverity(LinterRuleCategory.COMPOSITION, severity)
              }
              onApplyBulkSeverity={() =>
                onBulkUpdateSeverity(
                  LinterRuleCategory.COMPOSITION,
                  filteredRulesByCategory(LinterRuleCategory.COMPOSITION),
                )
              }
            />
          )}

          {/* Other rules */}
          <RuleGroup
            graphRef={graphRef}
            title="Other rules"
            description={
              <>
                These rules define conventions for the entire schema and
                directive usage outside of composition.
              </>
            }
            isVariantConfig={isVariantConfig}
            rules={filteredRulesByCategory(LinterRuleCategory.OTHER)}
            category={LinterRuleCategory.OTHER}
            bulkSeverity={categorySeverity[LinterRuleCategory.OTHER]}
            allVariants={allVariants}
            onSelectSeverity={onSelectNewSeverity}
            onSelectBulkSeverity={(severity) =>
              onSetCategorySeverity(LinterRuleCategory.OTHER, severity)
            }
            onApplyBulkSeverity={() =>
              onBulkUpdateSeverity(
                LinterRuleCategory.OTHER,
                filteredRulesByCategory(LinterRuleCategory.OTHER),
              )
            }
          />
        </Accordion>
      </CardBody>
    </Card>
  );
};

function RuleGroup({
  graphRef,
  title,
  description,
  isVariantConfig,
  rules,
  category,
  bulkSeverity,
  allVariants,
  onSelectSeverity,
  onSelectBulkSeverity: onBulkSelectSeverity,
  onApplyBulkSeverity,
}: {
  graphRef: GraphRef;
  title: string;
  description: JSX.Element;
  isVariantConfig: boolean;
  rules: FormValues['rules'];
  category: LinterRuleCategory;
  bulkSeverity: LintDiagnosticLevel | undefined;
  allVariants: GraphQLTypes.LinterConfigPageQuery_service_variants[];
  onSelectSeverity: (
    rule: GraphQLTypes.LintRule,
  ) => (newSeverity: GraphQLTypes.LintDiagnosticLevel) => void;
  onSelectBulkSeverity: (severity: GraphQLTypes.LintDiagnosticLevel) => void;
  onApplyBulkSeverity: () => void;
}) {
  const federatedVariants = allVariants.filter(
    (variant) => variant.hasManagedSubgraphs,
  );

  const variantsWithSupportForCompositionRules = federatedVariants.filter(
    (variant) =>
      variant.buildConfig?.buildPipelineTrack &&
      !compositionRulesUnsupportedBPTs.includes(
        variant.buildConfig?.buildPipelineTrack,
      ),
  );

  const disableSettingSeverity =
    !variantsWithSupportForCompositionRules.length &&
    category === LinterRuleCategory.COMPOSITION;

  // If all variants are updated to > 2.5, we can stop showing
  // any banners and allow updating severity for composition
  const showFederationUpgradeNoticeForCompositionRules =
    category === LinterRuleCategory.COMPOSITION &&
    variantsWithSupportForCompositionRules.length !== federatedVariants.length;

  const isCompositionRulesSupportedForCurrentVariant =
    !compositionRulesUnsupportedBPTs.includes(
      federatedVariants.find(
        (variant) => variant.name === graphRef.graphVariant,
      )?.buildConfig?.buildPipelineTrack ?? BuildPipelineTrack.FED_2_0,
    );

  const isBulkEditDisabled = rules.length === 0 || disableSettingSeverity;

  return (
    <AccordionItem>
      <AccordionHeader>
        <Group align="start">
          {!isVariantConfig && <AccordionIcon />}
          <Group orientation="vertical">
            <Group>
              <AccordionTitle>{title}</AccordionTitle>
              <Badge variant="neutralOutlined">{rules.length}</Badge>

              {showFederationUpgradeNoticeForCompositionRules && (
                <Tooltip
                  placement="top"
                  label="Upgrade required to use this feature."
                >
                  <Link
                    to={
                      (isCompositionRulesSupportedForCurrentVariant
                        ? graphSettingsRouteConfig.location({
                            graphId: graphRef.graphId,
                            graphVariant: graphRef.graphVariant,
                            tab: 'general',
                          })
                        : variantSettingsRouteConfig.location({
                            graphId: graphRef.graphId,
                            graphVariant: graphRef.graphVariant,
                            tab: 'general',
                          })) as LocationDescriptor
                    }
                  >
                    <IconStarSolid className="block cursor-pointer text-icon-primary" />
                  </Link>
                </Tooltip>
              )}
            </Group>

            <AccordionDescription>{description}</AccordionDescription>

            {showFederationUpgradeNoticeForCompositionRules && (
              <Alert status="info" className="mt-3">
                {variantsWithSupportForCompositionRules.length > 0 ? (
                  <>
                    Composition rules are available for only{' '}
                    <Tooltip
                      placement="top"
                      label={`${variantsWithSupportForCompositionRules
                        .slice(0, 4)
                        .map((variant) => variant.name)
                        .join(', ')}${
                        variantsWithSupportForCompositionRules.length - 4 > 0
                          ? ` and ${
                              variantsWithSupportForCompositionRules.length - 4
                            } other${
                              variantsWithSupportForCompositionRules.length -
                                4 >
                              1
                                ? 's'
                                : ''
                            }...`
                          : ''
                      }`}
                      className="ml-2 max-w-fit"
                    >
                      <b>
                        {variantsWithSupportForCompositionRules.length} out of{' '}
                        {federatedVariants.length}{' '}
                      </b>
                    </Tooltip>{' '}
                    federated variants, as this feature is supported only for
                    federation version 2.4 or later. To use this feature on the
                    remaining variants,
                  </>
                ) : (
                  <>
                    Composition rules are available for graphs on federation
                    version <b>2.4 or newer</b>. To use this feature,
                  </>
                )}{' '}
                <Link
                  to={
                    (isCompositionRulesSupportedForCurrentVariant
                      ? graphSettingsRouteConfig.location({
                          graphId: graphRef.graphId,
                          graphVariant: graphRef.graphVariant,
                          tab: 'general',
                        })
                      : variantSettingsRouteConfig.location({
                          graphId: graphRef.graphId,
                          graphVariant: graphRef.graphVariant,
                          tab: 'general',
                        })) as LocationDescriptor
                  }
                >
                  upgrade your federation version.
                </Link>
                <IconArrowRight className="ml-1" />
              </Alert>
            )}
          </Group>
          {!isVariantConfig && (
            <ButtonGroup>
              <BulkSeveritySelector
                disabled={isBulkEditDisabled}
                value={bulkSeverity}
                onSelect={(newSeverity) => onBulkSelectSeverity(newSeverity)}
              />
              <Button
                isDisabled={isBulkEditDisabled || bulkSeverity === undefined}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onApplyBulkSeverity();
                }}
              >
                Apply
              </Button>
            </ButtonGroup>
          )}
        </Group>
        {isVariantConfig && <AccordionIcon />}
      </AccordionHeader>

      <AccordionPanel>
        {rules.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-4">
            <IconEmptyStateMoon className="text-icon-primary" />
            <span className="text-secondary">
              No results found, try adjusting your search query.
            </span>
          </div>
        ) : (
          <TableContainer>
            <Table variant="striped">
              <Thead>
                <Tr>
                  <Th>Rule name</Th>
                  <Th isActionsMenu className="w-[100px]">
                    Severity
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {rules.map((rule) => (
                  <Tr key={rule.rule} id={rule.rule}>
                    <Td>
                      <div className="flex items-center pb-2">
                        <Code className="mr-1">{rule.rule}</Code>
                        {(rule.goodExampleCode || rule.badExampleCode) && (
                          <Tooltip
                            placement="end"
                            interactive
                            label={
                              <ExampleCode
                                goodExampleCode={rule.goodExampleCode}
                                badExampleCode={rule.badExampleCode}
                              />
                            }
                            className="max-w-fit"
                          >
                            <IconInfo className="cursor-pointer text-icon-primary" />
                          </Tooltip>
                        )}
                      </div>
                      <LinterRuleDescriptionMarkdownRenderer
                        descriptionMarkdown={rule.description}
                      />
                    </Td>
                    <Td isActionsMenu>
                      <SeveritySelector
                        value={
                          disableSettingSeverity
                            ? LintDiagnosticLevel.IGNORED
                            : rule.level
                        }
                        onSelect={onSelectSeverity(rule.rule)}
                        disabled={isVariantConfig || disableSettingSeverity}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
}
